import { KeyValue } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NativeDateAdapter } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFEducationHistoryDomain, AF_CONSTANTS, Attachment } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { EducationService } from '../service/education.service';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, ProgressBarEvent, ValidateAndNavigateEvent, SkipToMainContentEvent, AttachmentUploadedEvent, ShowErrorEvent } from '../service/events';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { MatSelectionListChange } from '@angular/material/list';
import { EducationDialogComponent } from './education-dialog/education-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FileDialogComponent } from '../file-dialog/file-dialog.component';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'app-education-history',
  templateUrl: './education-history.component.html',
  styleUrls: ['./education-history.component.scss'],
  standalone: false
})
export class EducationHistoryComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  educationData: AFEducationHistoryDomain;
  educationGroup: UntypedFormGroup;
  step: number = 0;
  educationInstruction: string;
  sticky: boolean = false;
  menuPosition: any;
  border: boolean = false;
  containerWidth: number;
  educationHistoryChange: boolean = false;
  listCheck: boolean = false;
  count: number = 3;
  jobCountry: string;
  showDegreeRequiredMessage: boolean = false;

  @ViewChild("stickyDiv") stickyDivElement: ElementRef;
  @ViewChildren('stickyDiv', { read: ViewContainerRef }) viewContainerRefs: QueryList<any>;
  @ViewChild("addEducationElement") addEducationElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;

  dataSource = new MatTableDataSource<Attachment>();
  displayedColumns: string[] = ['fileName', 'descOfFileName', 'fileType', 'fileSize', 'attachedBy', 'attachedDateString', 'remove'];

  regexStr: RegExp = /^[a-zA-Z0-9\s-&().,'"\\/:;]*$/;
  regexGpa: RegExp = /^$|^[0-4].\d{1,2}$/;

  constructor(private readonly userService: AuthService, private readonly educationService: EducationService, private readonly fb: UntypedFormBuilder, private readonly eventBus: EventService,
    private readonly _router: Router, private readonly _snackBar: MatSnackBar, private readonly customValidator: CustomValidatorsService, public dialog: MatDialog) {
    super(eventBus, _router);
  }

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
    this.jobCountry = this.userService.getJobCountry();
    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/educationHistory') {
          this.submit.setValue(false);
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitEducationHistory((this.educationGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitEducationHistory((this.educationGroup.value), event);
          } else if (this.validate() && this.validateDegreeRequiredMessage()) {
            this.submitEducationHistory((this.educationGroup.value), event);
          }
        }
      }
    );
  }

  showDegreeRequiredMessageDialog() {
    const dialogMsg = "A degree must be awarded prior to starting in this role.  The degree will be verified as part of the background screening process.  Please contact your recruiter with any questions regarding this requirement.";
    this.eventBus.emit(new ShowErrorEvent({ httpStatus: 1, text: dialogMsg }));
  }


  validateDegreeRequiredMessage() {
    if (this.educationData.degreeRequiredFlag != '0') {
      return true;
    }

    if (!this.showDegreeRequiredMessage) {
      for (let edu of this.educationList.controls) {
        if (edu.get("isCompleted").value == 1) {
          this.showDegreeRequiredMessage = true;
          break;
        }
      }
    }
    if (!this.showDegreeRequiredMessage) {
      this.showDegreeRequiredMessageDialog();
      this.showDegreeRequiredMessage = true;
      return false;
    }
    return this.showDegreeRequiredMessage;
  }

  subscribeEvents() {
    this.educationService.getEducationHistory().subscribe({
      next: (data: AFEducationHistoryDomain) => {
        this.educationData = data;
        if (this.userService.getJobCountry() && this.userService.getJobCountry() !== 'IND') {
          this.addInstruction(this.educationData);
          this.count = 3;
        } else {
          this.count = 4;
        }
        this.populateEducationHistory(this.educationData);
        this.createEducationDialog();
        this.dataSource.data = data.attachmentList;
        this.dataSource._updateChangeSubscription();
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/educationHistory") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );

    this.eventBus.on(
      AttachmentUploadedEvent,
      (event): void => {
        if (event.payload.file) {
          let attachment = event.payload.file as Attachment;
          this.dataSource.data.push(attachment);
          this.dataSource._updateChangeSubscription();

          if (event.payload.file.typeOfAttachmentId) {
            this.educationList.at(this.step).get("mongoAttachmentId").setValue(attachment.mongoAttachmentId);
            setTimeout(() => {
              let progress = this.getPercentage(this.educationGroup);
              this.eventBus.emit(new ProgressBarEvent({ id: progress }));
            }, 100);
          }
        }
      });
  }

  onFocusOutMainContent(event: any) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  addInstruction(educationData: AFEducationHistoryDomain) {

    if (educationData.secFlag == '0') {
      if (educationData.degreeRequiredFlag == '0') {
        this.educationInstruction = 'Please provide all formal education after high school, including degrees earned and partial attendance. If you have no formal education after high school, please provide your high school education information.';
      } else {
        this.educationInstruction = `Please provide all formal education after high school, including degrees earned and partial attendance. 
        If you have no formal education after high school, please provide your high school education information. 
        If you have no high school education or no formal education after high school, please select the box for "I have no education history to list.`;
      }
    } else if (educationData.finraRegisteredFlag == '0' || educationData.lockBoxFlag == '0') {
      if (educationData.degreeRequiredFlag == '0') {
        this.educationInstruction = `Please provide any education attempted or completed in the the last 10 years. If you have not attempted or completed any education within this timeframe, 
          please provide the highest level of education you have completed. If you are expected to graduate in the the next 6 months, you may list that degree with the expected date of graduation.`;
      } else {
        this.educationInstruction = `Please provide any education attempted or completed in the  last 10 years. If you have not attempted or completed any education within this timeframe, 
          please select the check box for "I have no education history to list.`;
      }
    } else if (this.userService.getEducationHistoryInstruction()) {
      this.educationInstruction = this.userService.getEducationHistoryInstruction();
    } else {
      this.educationInstruction = `Please provide the highest level of education you have completed. If you are expected to graduate in the next 6 months, you may list that degree with the expected date of graduation.`;
    }
  }


  submitEducationHistory(educationHistory, event) {
    this.educationService.submitEducationHistory(educationHistory).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }

      },
      error: (error: any) => console.log(error)
    });

  }

  setStep(index: number) {
    this.step = index;
  }

  get educationList() { return this.educationGroup.get('educationList') as UntypedFormArray; }
  get verifyId() { return this.educationGroup.get('verifyId') }
  get submit() { return this.educationGroup.get('submit') }
  get noEducationHistory() { return this.educationGroup.get('noEducationHistory') }
  get lockBox() { return this.educationGroup.get('lockBox') }
  get degreeRequiredFlag() { return this.educationGroup.get('degreeRequiredFlag') }
  get campusHireFlag() { return this.educationGroup.get('campusHireFlag') }
  get secFlag() { return this.educationGroup.get('secFlag') }
  get usTrustBioEmail() { return this.educationGroup.get('usTrustBioEmail') }
  get lockBoxFlag() { return this.educationGroup.get('lockBoxFlag') }

  get bookmarkUrl() { return this.educationGroup.get('bookmarkUrl') };

  populateEducationHistory(educationData: AFEducationHistoryDomain) {
    this.educationGroup.patchValue(educationData);
    for (let address of this.educationData.educationList) {
      let fg: UntypedFormGroup = this.fieldsGroup();
      fg.patchValue(address);
      this.educationList.push(fg);
      this.setValidators(fg);
    }
    if (this.educationList.length == 0) {
      this.createComponent();
    }
  }

  setValidators(control: UntypedFormGroup) {

    const country = control.get('educationInstitutionCountry');
    const usState = control.get('educationInstitutionUsState');
    const state = control.get('educationInstitutionState');

    this.setStateValidators(country.value, usState, state);

    if (this.educationData.jobCountryId == 78) {
      control.get('attachmentTypeId').setValidators(Validators.required);
      control.get('mongoAttachmentId').setValidators(Validators.required);
    } else {
      control.get('attachmentTypeId').setValidators(null);
      control.get('mongoAttachmentId').setValidators(null);
    }

    country.valueChanges
      .subscribe(country => {
        this.setStateValidators(country, usState, state);
      });

  }

  setEndDateValidators(value: string, endDate: AbstractControl) {
    if (value === '1') {
      endDate.setValidators([Validators.required]);
    } else {
      endDate.setValidators(null);
      endDate.setErrors(null);
    }
    endDate.updateValueAndValidity();
  }

  setStateValidators(value: string, usState: AbstractControl, state: AbstractControl) {
    if (value === "186") {
      usState.setValidators([Validators.required]);
      state.setValidators(null);
      state.setErrors(null);
    } else {
      state.setValidators([Validators.required, this.customValidator.validateRequiredField]);
      usState.setValidators(null);
      usState.setErrors(null);
    }
    usState.updateValueAndValidity();
    state.updateValueAndValidity();
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      educationInstitutionName: [
        '',
        [
          Validators.required,
          Validators.pattern(this.regexStr),
          Validators.minLength(1),
          Validators.maxLength(200),
          this.customValidator.validateRequiredField
        ]
      ],
      educationInstitutionType: [
        '',
        [
          Validators.required
        ]
      ],
      course: [
        '',
        [
          Validators.required,
          Validators.pattern(this.regexStr),
          Validators.minLength(1),
          Validators.maxLength(200),
          this.customValidator.validateRequiredField
        ]
      ],
      educationInstitutionZip: [
        '',
        [
          Validators.required,
          Validators.pattern(this.regexStr),
          Validators.minLength(1),
          Validators.maxLength(25),
          this.customValidator.validateRequiredField
        ]
      ],
      educationInstitutionCity: [
        '',
        [
          Validators.required,
          Validators.pattern(this.regexStr),
          Validators.minLength(1),
          Validators.maxLength(200),
          this.customValidator.validateRequiredField
        ]
      ],
      educationInstitutionState: [
        '',
        [Validators.required,
        Validators.pattern(this.regexStr),
        Validators.minLength(1),
        Validators.maxLength(200),
        this.customValidator.validateRequiredField
        ]
      ],
      educationInstitutionUsState: [
        '',
        [
          Validators.required
        ]
      ],
      educationInstitutionCountry: [
        '',
        [
          Validators.required
        ]
      ],
      isCompleted: [
        '', 
        [
          Validators.required
        ]
      ],
      educationStartDate: [
        '',
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(1)
        ]
      ],
      educationEndDate: [
        '', 
        [
          Validators.maxLength(10)
        ]
      ],
      degreeType: [
        '', 
        [Validators.required]
      ],
      enrollmentNumber: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(100),
          this.customValidator.validateRequiredField
        ]
      ],
      seatNumber: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(100),
          this.customValidator.validateRequiredField
        ]
      ],
      registrationNumber: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(100),
          this.customValidator.validateRequiredField
        ]
      ],
      gradeGpa: [
        '', 
        [
          Validators.pattern(this.regexGpa)
        ]
      ],
      degreeCopy: [
        '', 
        [
          Validators.required
        ]
      ],
      graduationDate: [''],
      nameWhileAttending: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(100)
        ]
      ],
      commt: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(1000)
        ]
      ],
      eduInstStreetAddress1: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(300),
          this.customValidator.validateRequiredField
        ]
      ],
      eduInstStreetAddress2: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(155)
        ]
      ],
      eduInstStreetAddress3: [
        '',
        [
          Validators.pattern(this.regexStr),
          Validators.maxLength(155)
        ]
      ],
      radioCheck: [false],
      radioCheckTwo: [false],
      attachmentTypeId: [''],
      mongoAttachmentId: ['']
    }, {
      validators: educationDatesValidator()
    })
  }

  allowOnlyAlphaNumeric(event: any) {
    const charcode = event.which ?? event.keyCode;
    let inp = String.fromCharCode(charcode);
    if (/[a-zA-Z0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  onAddEducation() {
    setTimeout(() => {
      this.addEducationElement.nativeElement.focus();
      this.border = true;
    });
    this.createComponent();
  }

  createComponent() {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.educationList.push(fg);
    this.step = this.educationList.length - 1;
    this.submit.setValue(false);
    this.setValidators(fg);
  }

  validate() {
    this.submit.setValue(true);
    if (this.noEducationHistory.value) {
      return true;
    }
    this.educationGroup.markAllAsTouched();
    if (this.educationGroup?.invalid) {
      let isInvalid = true;
      const firstInvalidField = Object.keys(this.educationGroup.controls).find(field => this.educationGroup.get(field)?.invalid);
      if (this.educationGroup.controls[firstInvalidField] instanceof FormArray) {
        (this.educationGroup.controls[firstInvalidField]).controls.forEach((data: FormGroup) => {
          if (data.invalid && isInvalid) {
            this.userService.setFocus(data);
            isInvalid = false;
          }
        })
      } else {
        this.userService.setFocus(this.educationGroup);
      }
    }
    if (this.jobCountry == 'IND' && this.getPercentage(this.educationGroup) < 100) {
      return false;
    }

    return !this.educationGroup.invalid;
  }

  getPercentage(educationGroup: UntypedFormGroup): number {

    let errorCount = 0;
    let totalCount = 0;
    let isGbsInd = (this.jobCountry == 'IND') ? 1 : 0;
    let channelArray: Array<string> = ['commt'];

    if (this.noEducationHistory.value) {
      return 100;
    }

    for (let container of educationGroup.get('educationList')['controls']) {
      for (let controlKey in container.controls) {
        if (channelArray.some(x => x === controlKey)) {
          continue;
        }
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
          // we need to ensure any one of the following field is updated or not
          if (this.jobCountry == 'IND' && (controlKey == 'enrollmentNumber' || controlKey == 'seatNumber' || controlKey == 'registrationNumber')) {
            if (container.controls[controlKey].value) {
              isGbsInd = 0;
            }
          }
        }
      }
    }
    if (isGbsInd) {
      errorCount = errorCount + isGbsInd;
    }
    let percentage = ((totalCount - errorCount) / totalCount) * 100;

    return percentage;
  }

  createEducationDialog() {
    let jobCountryCode = this.userService.getJobCountry();
    if (jobCountryCode && (jobCountryCode !== 'USA')) {
      // invoke pop-up
      const dialogRef = this.dialog.open(EducationDialogComponent, {
        ariaLabel: 'Education Information Dialog',
        role: 'alertdialog',
        width: '550px',
        autoFocus: false,
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(() => {
        window.scrollTo(0, 0);
      });
    }
  }

  ngAfterViewInit() {
    this.handleZoom();

    this.viewContainerRefs.changes.subscribe(() => {
      if (this.viewContainerRefs.toArray().length) {
        if (this.stickyDivElement) {
          this.containerWidth = this.stickyDivElement.nativeElement.clientWidth;
        }
      }
    });

    this.educationGroup.valueChanges.subscribe(() => {
      if (this.educationList.length != 0) {
        this.listCheck = true;
      } else {
        this.listCheck = false;
      }

      setTimeout(() => {
        let progress = this.getPercentage(this.educationGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/educationHistory");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  handleSelection(event: MatSelectionListChange) {
    event.source.deselectAll();
    event.options[0].selected = true;
    this.step = event.options[0].value;
  }

  deleteClick(i) {

    if (this.educationList.at(i).value.mongoAttachmentId) {
      this.removeAttachment(this.educationList.at(i).value.mongoAttachmentId, this.educationList.at(i).value.attachmentTypeId);
    }
    this.educationList.removeAt(i);
    if (i == this.educationList.length) {
      this.step = i - 1;
    }

    setTimeout(() => {
      if (this.educationList && this.educationList.length == 0) {
        this.addButtonElement.nativeElement.focus();
        this.border = false;
      } else {
        this.addEducationElement.nativeElement.focus();
        this.border = true;
      }
    });
  }

  focusTarget() {
    setTimeout(() => {
      this.addEducationElement.nativeElement.focus();
      this.border = true;
    });
  }

  @HostListener('document:keydown', ['$event'])
  clearBorder(keyDown: KeyboardEvent) {
    if (keyDown.key === 'Tab') {
      this.border = false;
    }
  }

  @HostListener('click', ['$event'])
  clearBorderClick() {
    this.border = false;
  }

  completedOnBlur(i: number) {
    if (this.educationList.at(i).get('isCompleted').value) {
      this.educationList.at(i).get('radioCheck').setValue(false);
    } else {
      this.educationList.at(i).get('radioCheck').setValue(true);
    }
  }

  completedOnFocus(i: number) {
    this.educationList.at(i).get('radioCheck').setValue(false);
  }

  copyOnBlur(i: number) {
    if (this.educationList.at(i).get('degreeCopy').value) {
      this.educationList.at(i).get('radioCheckTwo').setValue(false);
    } else {
      this.educationList.at(i).get('radioCheckTwo').setValue(true);
    }
  }

  copyOnFocus(i: number) {
    this.educationList.at(i).get('radioCheckTwo').setValue(false);
  }

  dropDownScreenReaderInstitution(education: any): string {
    if (education.controls.educationInstitutionType.value) {
      return 'Selected ' + this.educationData.typeOfEducationInstLookup[Object.keys(this.educationData.typeOfEducationInstLookup).find(e => e === education.controls.educationInstitutionType.value)];
    } else {
      return 'blank';
    }
  }
  dropDownScreenReaderDegree(education: any): string {
    if (education.controls.degreeType.value) {
      return 'Selected ' + this.educationData.typeOfDegreeLookup[Object.keys(this.educationData.typeOfDegreeLookup).find(e => e === education.controls.degreeType.value)];
    } else {
      return 'blank';
    }
  }
  dropDownScreenReaderCountry(education: any): string {
    if (education.controls.educationInstitutionCountry.value) {
      return 'Selected ' + this.educationData.countryLookup[Object.keys(this.educationData.countryLookup).find(e => e === education.controls.educationInstitutionCountry.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderState(education: any): string {
    if (education.controls.educationInstitutionUsState.value) {
      return 'Selected ' + this.educationData.usStateLookup[Object.keys(this.educationData.usStateLookup).find(e => e === education.controls.educationInstitutionUsState.value)];
    } else {
      return 'blank';
    }
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }


  idAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {

    let keyA: number = Number(a.key);
    let keyB: number = Number(b.key);

    if (keyA > keyB) {
      return 1;
    }
    if (keyA < keyB) {
      return -1;
    }
    return 0;
  }

  valueAscOrderOtherLast = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    try {
      if (a.value == "Other") {
        return 1;
      }
      if (b.value == "Other") {
        return -1;
      }
      return a.value.localeCompare(b.value);
    } catch (e) {
      console.log(e);
    }
  }

  createGroup() {
    this.educationGroup = this.fb.group({
      verifyId: [this.userService.getVerifyId()],
      usTrustBioEmail: [''],
      secFlag: [''],
      educationLimit: [],
      lockBoxFlag: [''],
      campusHireFlag: [''],
      degreeRequiredFlag: [''],
      lockBox: [false],
      noEducationHistory: [false],
      submit: [false],
      bookmarkUrl: [''],
      educationList: new UntypedFormArray([])
    });
  }

  @HostListener('window:resize', ['$event'])
  @HostListener('window:load', ['$event'])
  handleZoom() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    if (windowWidth <= AF_CONSTANTS.windowWidth || windowHeight <= AF_CONSTANTS.windowHeight) {
      this.educationHistoryChange = true;
    } else {
      this.educationHistoryChange = false;
    }
  }
  @HostListener('window:scroll', ['$event'])
  handleScroll() {
    const windowScroll = window.pageYOffset;
    if (windowScroll >= AF_CONSTANTS.windowScroll_Lower_limit && windowScroll <= AF_CONSTANTS.windowScroll_Upper_limit_education) {
      this.sticky = true;
    } else {
      this.sticky = false;
    }

  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  openFileDialog(ousAttachments, ousAttachmentId, type) {

    const dialogRef = this.dialog.open(FileDialogComponent, {
      width: '550px',
      role: 'alertdialog',
      data: {
        ousAttachments: ousAttachments,
        ousAttachmentId: ousAttachmentId,
        ousAttachmentDesc: "educationhistory"
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      if (ousAttachments) {
        this.dataSource._updateChangeSubscription();
        setTimeout(() => {
          this.addEducationElement.nativeElement.focus();
        });
      }
    });
  }

  removeAttachment(attachmentId, typeOfAttachmentId) {
    this.userService.removeMongoAttachment(attachmentId).subscribe({
      next: () => {
        this._snackBar.open("Attachment removed successfully", "Close", {
          duration: 4000,
        });

        this.dataSource.data = this.dataSource.data.filter((value, key) => {
          if (value.mongoAttachmentId) {
            return value.mongoAttachmentId != attachmentId;
          } else {
            return value.attachmentId != attachmentId;
          }
        });

        this.dataSource._updateChangeSubscription();

        for (let edu of this.educationList.controls) {
          if (edu.get("mongoAttachmentId").value == attachmentId) {
            this.educationService.removeEducationHisAttachment(attachmentId).subscribe(
              () => {
                edu.get("mongoAttachmentId").setValue("");
                edu.get("attachmentTypeId").setValue("");
              });
            break;
          }
        }

        setTimeout(() => {
          let progress = this.getPercentage(this.educationGroup);
          this.eventBus.emit(new ProgressBarEvent({ id: progress }));
        }, 100);

      },
      error: (error: any) => console.log(error)
    });
  }

  downloadAttachment(attachmentId: string, fileName: string): void {
    this.userService.downloadAttachment(attachmentId, fileName).subscribe();
  }

}

export function educationDatesValidator(): ValidatorFn {
  return (control: UntypedFormGroup): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    const endDate = control.get('educationEndDate');
    const startDate = control.get('educationStartDate');

    if (startDate.value && endDate.value && (startDate.value > endDate.value)) {
      endDate.setErrors({ noMatch: true });
    }
    return Object.keys(errors).length ? errors : null;
  };
}

export class MyDateAdapter extends NativeDateAdapter {

  format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    } else {
      return date.toDateString();
    }
  }

  parse(value: any): Date | null {
    if ((typeof value === 'string') && (value.indexOf('-') > -1)) {
      const str = value.split('-');
      const dayArray = str[2].split('T');
      const year = Number(str[0]);
      const month = Number(str[1]) - 1;
      const day = Number(dayArray[0]);

      console.log("date example", new Date(year, month, day));
      return new Date(year, month, day);
    }
    const timestamp = typeof value === 'number' ? value : Date.parse(value);
    return isNaN(timestamp) ? null : new Date(timestamp);
  }
}






